import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { PageActions } from "./PageActions";

const SOURCE_URL = "https://github.com/HeiselAnalytics/sailplot";
const LICENSE_URL = `${SOURCE_URL}/blob/main/LICENSE`;
const ORIGINAL_PROJECT_URL = "https://sourceforge.net/projects/boats/";

const copy = {
  en: {
    eyebrow: "Open Source",
    title: "Open by design.",
    intro:
      "The SailPlot editor is open source under the GNU General Public License version 3. Its source, license, and origins remain public and verifiable.",
    licenseLabel: "Open-source license",
    licenseName: "GPL",
    licenseVersion: "Version 3",
    promiseEyebrow: "What that means",
    promiseTitle: "Free to inspect, use, and improve.",
    promiseText:
      "The editor core remains open source. Paid SailPlot packages cover your visual identity, its implementation, managed hosting, and your dedicated SailPlot address, not access to hidden editor features.",
    sourceTitle: "Source code",
    sourceText:
      "Inspect the current implementation, follow its development, or build from the public SailPlot repository.",
    sourceAction: "Open source code",
    licenseTitle: "GPLv3 license",
    licenseText:
      "Read the complete license terms governing use, modification, and redistribution of the SailPlot source code.",
    licenseAction: "Read license",
    originalTitle: "Original BOATS project",
    originalText:
      "SailPlot was inspired by the historical open-source BOATS race-scenario drawing tool. SailPlot is a new web implementation and does not use BOATS as a runtime dependency.",
    originalAction: "View BOATS on SourceForge",
  },
  de: {
    eyebrow: "Open Source",
    title: "Offen entwickelt.",
    intro:
      "Der SailPlot-Editor ist unter der GNU General Public License Version 3 als Open Source verfügbar. Quellcode, Lizenz und Ursprung bleiben öffentlich und nachvollziehbar.",
    licenseLabel: "Open-Source-Lizenz",
    licenseName: "GPL",
    licenseVersion: "Version 3",
    promiseEyebrow: "Was das bedeutet",
    promiseTitle: "Einsehen, nutzen und weiterentwickeln.",
    promiseText:
      "Der Editor-Kern bleibt Open Source. Kostenpflichtige SailPlot-Pakete umfassen deinen Markenauftritt, dessen Umsetzung, das verwaltete Hosting und deine eigene SailPlot-Adresse, nicht den Zugang zu versteckten Editor-Funktionen.",
    sourceTitle: "Quellcode",
    sourceText:
      "Prüfe die aktuelle Umsetzung, verfolge die Entwicklung oder erstelle eine eigene Version aus dem öffentlichen SailPlot-Repository.",
    sourceAction: "Quellcode öffnen",
    licenseTitle: "GPLv3-Lizenz",
    licenseText:
      "Lies die vollständigen Bedingungen für Nutzung, Veränderung und Weitergabe des SailPlot-Quellcodes.",
    licenseAction: "Lizenz lesen",
    originalTitle: "Ursprüngliches BOATS-Projekt",
    originalText:
      "SailPlot wurde vom historischen Open-Source-Werkzeug BOATS zum Zeichnen von Regattasituationen inspiriert. SailPlot ist eine neue Web-Implementierung und verwendet BOATS nicht als Laufzeitabhängigkeit.",
    originalAction: "BOATS auf SourceForge öffnen",
  },
} as const;

export function OpenSourcePage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];

  return (
    <article className="platform-page platform-reference-page platform-open-source-page">
      <header className="platform-open-source-hero">
        <div>
          <span className="platform-page-eyebrow">{text.eyebrow}</span>
          <h1>{text.title}</h1>
          <p>{text.intro}</p>
        </div>
        <div
          className="platform-open-source-license"
          aria-label={`${text.licenseName} ${text.licenseVersion}`}
        >
          <span>{text.licenseLabel}</span>
          <strong>{text.licenseName}</strong>
          <small>{text.licenseVersion}</small>
        </div>
      </header>

      <section className="platform-open-source-promise">
        <div>
          <span className="platform-page-eyebrow">{text.promiseEyebrow}</span>
          <h2>{text.promiseTitle}</h2>
        </div>
        <p>{text.promiseText}</p>
      </section>

      <div className="platform-resource-grid platform-resource-grid--three">
        <article className="platform-open-source-resource">
          <span className="platform-open-source-resource__number">01</span>
          <strong>{text.originalTitle}</strong>
          <p>{text.originalText}</p>
          <a
            href={ORIGINAL_PROJECT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.originalAction} ↗
          </a>
        </article>
        <article className="platform-open-source-resource">
          <span className="platform-open-source-resource__number">02</span>
          <strong>{text.sourceTitle}</strong>
          <p>{text.sourceText}</p>
          <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer">
            {text.sourceAction} ↗
          </a>
        </article>
        <article className="platform-open-source-resource">
          <span className="platform-open-source-resource__number">03</span>
          <strong>{text.licenseTitle}</strong>
          <p>{text.licenseText}</p>
          <a href={LICENSE_URL} target="_blank" rel="noopener noreferrer">
            {text.licenseAction} ↗
          </a>
        </article>
      </div>
      <PageActions language={language} navigate={navigate} />
    </article>
  );
}
